/**
 * Kickard PHASE 5.0B-1 — Visual Identity Foundation primitives.
 *
 * Thin React wrappers over the token / class layer defined in
 * `index.css` (`.kk-*`). Every screen can adopt these piecemeal without
 * touching Phase 5.0A information architecture, gameplay, matchmaking
 * or engine code.
 *
 * These primitives are PRESENTATION ONLY:
 *   - No routing.
 *   - No data fetching.
 *   - No engine access.
 *   - No cosmetic gameplay side-effects.
 *
 * The `.kk-*` token/class layer is the source of truth — components
 * here simply provide a typed React API around it. Change tokens in
 * `index.css` to retune the whole system; change these files only to
 * add semantic wrappers.
 */
import React from 'react';

// ---------- Backdrops ----------
// Wrap a whole screen so it inherits the correct Night Match mood.
// The backdrop pattern is CSS-only so failing to load imagery never
// leaves the app unreadable.

/**
 * StadiumBackdrop — deep dark sky, gold ambient at the top, subtle
 * pitch-green wash at the bottom, vignette. Use on marketing / home /
 * hero surfaces.
 */
export function StadiumBackdrop({ children, className = '', vignette = true, ...rest }) {
  return (
    <div
      className={
        'kk-backdrop kk-backdrop--stadium '
        + (vignette ? 'kk-vignette ' : '')
        + className
      }
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * PitchBackdrop — pitch-green wash with faint pitch-line grid. Use on
 * gameplay and penalty surfaces. Preserves Phase 5.0A card readability.
 */
export function PitchBackdrop({ children, className = '', ...rest }) {
  return (
    <div
      className={'kk-backdrop kk-backdrop--pitch ' + className}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * CompetitiveBackdrop — gold ambient glow + deep stadium fade. Use on
 * Ranked / Table / Weekly Cup / rank-reveal surfaces.
 */
export function CompetitiveBackdrop({ children, className = '', vignette = true, ...rest }) {
  return (
    <div
      className={
        'kk-backdrop kk-backdrop--competitive '
        + (vignette ? 'kk-vignette ' : '')
        + className
      }
      {...rest}
    >
      {children}
    </div>
  );
}

// ---------- Panels ----------

/**
 * Panel — Kickard broadcast-style panel.
 *
 *   variant:
 *     - 'default'      (elevated surface, subtle border)
 *     - 'strong'       (heavier surface for hero areas)
 *     - 'competitive'  (gold-tinged border for ranked / cup surfaces)
 *     - 'inset'        (recessed surface for inputs / secondary rows)
 *   edgeTop: when true, paints a faint gold top edge (championship
 *   highlight). Only meaningful on 'default' or 'strong'.
 */
export function Panel({
  children, variant = 'default', edgeTop = false,
  className = '', testId, ...rest
}) {
  const cls = 'kk-panel '
    + (variant === 'strong'      ? 'kk-panel--strong '      : '')
    + (variant === 'competitive' ? 'kk-panel--competitive ' : '')
    + (variant === 'inset'       ? 'kk-panel--inset '       : '')
    + className;
  return (
    <div className={cls} data-testid={testId} {...rest}>
      {edgeTop && <div className="kk-panel__edge-top" aria-hidden />}
      {children}
    </div>
  );
}

// ---------- Buttons ----------

/**
 * KKButton — Kickard button hierarchy.
 *
 *   variant:
 *     - 'primary'   → filled gold championship CTA (PLAY / PLAY RANKED)
 *     - 'secondary' → dark neutral CTA with clear tap affordance
 *     - 'tertiary'  → text-only action (Details / Luck Report)
 *     - 'danger'    → red-tinted destructive action (Reset)
 *
 * All variants render a semantic `<button>` (or `<a>` when `as="a"`).
 * `type="button"` is set by default to prevent accidental form submits.
 */
export function KKButton({
  children, variant = 'primary', className = '', testId,
  as = 'button', type, ...rest
}) {
  const Cmp = as;
  const cls = 'kk-btn '
    + (variant === 'primary'   ? 'kk-btn--primary '   : '')
    + (variant === 'secondary' ? 'kk-btn--secondary ' : '')
    + (variant === 'tertiary'  ? 'kk-btn--tertiary '  : '')
    + (variant === 'danger'    ? 'kk-btn--danger '    : '')
    + className;
  const t = Cmp === 'button' ? (type || 'button') : type;
  return (
    <Cmp className={cls} data-testid={testId} type={t} {...rest}>
      {children}
    </Cmp>
  );
}

// ---------- Segmented tabs ----------

/**
 * SegmentedTabs — broadcast-style segmented control.
 *
 *   items:   [{ id, label, testId? }]
 *   activeId: currently-active id
 *   onSelect(id): tab change handler
 *
 * Preserves the Phase 5.0A affordance rules: min 44 px tap height,
 * horizontal scroll on narrow phones, unmistakable active state
 * (gold pill), inactive tabs still visibly tappable.
 */
export function SegmentedTabs({ items, activeId, onSelect, testId, className = '' }) {
  return (
    <div
      className={'w-full overflow-x-auto no-scrollbar ' + className}
      data-testid={testId}
    >
      <div className="kk-seg" role="tablist">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            role="tab"
            aria-selected={it.id === activeId}
            data-active={it.id === activeId ? 'true' : 'false'}
            data-testid={it.testId || `kk-tab-${it.id}`}
            onClick={() => onSelect?.(it.id)}
            className="kk-seg__item"
          >
            {it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Competitive primitives ----------

/**
 * RankBadge — compact tier + SR chip. `iconSlot` is an optional
 * decorative <svg>/<img> slot for the future tier emblem (art asset).
 * Values still come from the existing Ranked APIs — this is
 * presentation only.
 */
export function RankBadge({ tierLabel, sr, iconSlot, testId }) {
  return (
    <div className="kk-rank-badge" data-testid={testId}>
      {iconSlot}
      <span className="kk-rank-badge__tier">{tierLabel || '—'}</span>
      {sr != null && <span className="kk-rank-badge__sr">{sr} SR</span>}
    </div>
  );
}

/**
 * FormChip — a single W / L / D chip for recent-form rows.
 * `result` accepts 'W' | 'L' | 'D'.
 */
export function FormChip({ result = 'D', testId }) {
  const upper = String(result).toUpperCase();
  return (
    <span className="kk-form-chip" data-result={upper} data-testid={testId}>
      {upper === 'W' || upper === 'L' ? upper : '·'}
    </span>
  );
}

/**
 * SRDelta — coloured Δ SR indicator. Positive → green, negative →
 * red, otherwise muted. Formatting is caller's responsibility so we
 * never re-compute SR here.
 */
export function SRDelta({ value, testId }) {
  const num = typeof value === 'number' ? value : Number(value);
  const sign = Number.isNaN(num) || num === 0
    ? 'neutral'
    : (num > 0 ? 'positive' : 'negative');
  const label = Number.isNaN(num)
    ? String(value ?? '—')
    : (num > 0 ? `+${num}` : `${num}`);
  return (
    <span className="kk-sr-delta" data-sign={sign} data-testid={testId}>
      {label} SR
    </span>
  );
}

// ---------- Profile / stat primitives ----------

/**
 * StatTile — Kickard broadcast-style stat tile: label + big value +
 * optional secondary line.
 */
export function StatTile({ label, value, sub, testId, className = '' }) {
  return (
    <div className={'kk-stat-tile ' + className} data-testid={testId}>
      <span className="kk-stat-tile__label">{label}</span>
      <span className="kk-stat-tile__value">{value ?? '—'}</span>
      {sub != null && <span className="kk-stat-tile__sub">{sub}</span>}
    </div>
  );
}

/**
 * ScoreDisplay — dominant Bebas score treatment for hero result areas
 * (post-match, penalty result). Renders "N-N" with a broadcast dash.
 */
export function ScoreDisplay({ p1, p2, testId, className = '' }) {
  return (
    <div className={'kk-score-display ' + className} data-testid={testId}>
      <span>{p1 ?? 0}</span>
      <span className="mx-3 text-white/40">–</span>
      <span>{p2 ?? 0}</span>
    </div>
  );
}
